import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FileSignature, Loader2, Plus, Send, Trash2, XCircle } from 'lucide-react';
import { apiRequest } from '../utils/api';
import ConfirmModal from '../components/shared/ConfirmModal';

const getResumeStep = (req) => {
  if (!req?.resolved_pdf_url) return 1;
  const hasSigners = (req.signers || []).some((s) => s.name?.trim() && s.email?.trim());
  if (!hasSigners) return 2;
  if (!(req.fields || []).length) return 3;
  return 4;
};

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Drafts' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'expired', label: 'Expired' },
];

const statusBadge = (status) => {
  const map = {
    draft: 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] border border-[var(--color-border)]',
    sent: 'bg-blue-500/15 text-blue-600 border border-blue-500/25',
    in_progress: 'bg-amber-500/15 text-[var(--color-warning)] border border-amber-500/25',
    completed: 'bg-green-500/15 text-[var(--color-success)] border border-green-500/25',
    expired: 'bg-red-500/15 text-[var(--color-danger)] border border-red-500/25',
    voided: 'bg-red-500/15 text-[var(--color-danger)] border border-red-500/25',
  };
  return map[status] || 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] border border-[var(--color-border)]';
};

const EsignDashboardPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [requests, setRequests] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voidTarget, setVoidTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listData, usageData] = await Promise.all([
        apiRequest(`esign/requests${tab !== 'all' ? `?tab=${tab}` : ''}`),
        apiRequest('esign/usage'),
      ]);
      setRequests(listData?.requests || []);
      setUsage(usageData);
    } catch (err) {
      toast.error(err.message || 'Failed to load eSign requests');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const remind = async (id) => {
    try {
      await apiRequest(`esign/requests/${id}/remind`, { method: 'POST' });
      toast.success('Reminders sent');
    } catch (err) {
      toast.error(err.message || 'Failed to send reminders');
    }
  };

  const voidRequest = async (id) => {
    try {
      await apiRequest(`esign/requests/${id}/void`, { method: 'POST' });
      toast.success('Request voided');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to void request');
    }
  };

  const regeneratePdf = async (id) => {
    try {
      await apiRequest(`esign/requests/${id}/regenerate`, { method: 'POST' });
      toast.success('Completed PDF regenerated');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to regenerate PDF');
    }
  };

  const deleteRequest = async (id) => {
    try {
      await apiRequest(`esign/requests/${id}`, { method: 'DELETE' });
      toast.success('Request deleted');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete request');
    }
  };

  const canDelete = (status) => ['draft', 'expired', 'voided'].includes(status);

  return (
    <div className="p-6 max-w-6xl mx-auto w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text)] flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-[var(--color-primary)]" />
            eSign
          </h1>
          <p className="text-[var(--color-text-muted)] mt-1 w-full">
            Send contracts for electronic signature. Pick documents from your library or upload a fresh PDF.
          </p>
          {usage && (
            <p className="text-sm text-[var(--color-text-muted)] mt-2">
              Envelopes this month: {usage.used} / {usage.limit}
            </p>
          )}
        </div>
        <Link
          to="/esign/new"
          className="brand-gradient-btn inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg"
        >
          <Plus className="w-4 h-4" />
          New E-Signature
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              tab === t.id
                ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]'
                : 'bg-[var(--color-surface)] text-[var(--color-text-secondary)] border border-[var(--color-border)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-[var(--color-text-muted)]">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : requests.length === 0 ? (
        <div className="elevated-card rounded-xl p-12 text-center">
          <FileSignature className="w-12 h-12 text-[var(--color-text-muted)] mx-auto mb-4" />
          <p className="text-[var(--color-text-muted)]">No eSign requests yet.</p>
          <Link to="/esign/new" className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] mt-2 inline-block font-medium">
            Create your first envelope
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] elevated-card">
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface-muted)]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Signers</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase">Updated</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)] bg-[var(--color-surface)]">
              {requests.map((req) => (
                <tr key={req.id} className="hover:bg-[var(--color-surface-muted)]">
                  <td className="px-4 py-3 text-[var(--color-text)] font-medium">{req.title}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs capitalize ${statusBadge(req.status)}`}>
                      {req.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-text-secondary)] text-sm">{req.signers?.length || 0}</td>
                  <td className="px-4 py-3 text-[var(--color-text-muted)] text-sm">
                    {req.updated_at ? new Date(req.updated_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {req.status === 'draft' && (
                      <button
                        type="button"
                        onClick={() => navigate(`/esign/${req.id}?step=${getResumeStep(req)}`)}
                        className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] text-sm"
                      >
                        Continue
                      </button>
                    )}
                    {req.status === 'completed' && req.completed_pdf_url && (
                      <>
                        <a
                          href={req.completed_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--color-success)] hover:opacity-80 text-sm"
                        >
                          Download
                        </a>
                        <button
                          type="button"
                          onClick={() => regeneratePdf(req.id)}
                          className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] text-sm"
                        >
                          Regenerate
                        </button>
                      </>
                    )}
                    {['in_progress', 'sent'].includes(req.status) && (
                      <>
                        <button
                          type="button"
                          onClick={() => remind(req.id)}
                          className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] text-sm inline-flex items-center gap-1"
                        >
                          <Send className="w-3 h-3" /> Remind
                        </button>
                        <button
                          type="button"
                          onClick={() => setVoidTarget(req)}
                          className="text-[var(--color-danger)] hover:opacity-80 text-sm inline-flex items-center gap-1"
                        >
                          <XCircle className="w-3 h-3" /> Void
                        </button>
                      </>
                    )}
                    {req.status !== 'draft' && (
                      <button
                        type="button"
                        onClick={() => navigate(`/esign/${req.id}`)}
                        className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-sm"
                      >
                        View
                      </button>
                    )}
                    {canDelete(req.status) && (
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(req)}
                        className="text-[var(--color-danger)] hover:opacity-80 text-sm inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Delete request?"
        message={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return;
          await deleteRequest(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
      <ConfirmModal
        open={Boolean(voidTarget)}
        title="Void request?"
        message="Signers will no longer be able to sign this document."
        confirmLabel="Void"
        cancelLabel="Cancel"
        destructive
        onCancel={() => setVoidTarget(null)}
        onConfirm={async () => {
          if (!voidTarget) return;
          await voidRequest(voidTarget.id);
          setVoidTarget(null);
        }}
      />
    </div>
  );
};

export default EsignDashboardPage;
